<script setup>
import { computed } from "vue";

const props = defineProps({
  card: {
    type: Object,
    required: true,
    validator: (value) => {
      // Only require that it's an object with at least these properties
      return (
        value &&
        typeof value === "object" &&
        "link" in value &&
        "img" in value &&
        "title" in value &&
        "text" in value
      );
    },
  },
  searchTerm: {
    type: String,
    default: "",
  },
});

const fallbackIcons = ["icon-crm.webp", "icon-laravel.webp"];

const shouldShowInitial = computed(() => {
  return (
    props.card.img &&
    fallbackIcons.some((icon) => props.card.img.includes(icon))
  );
});

const firstLetter = computed(() => {
  return props.card.title ? props.card.title.charAt(0).toUpperCase() : "";
});

// Generate a consistent color based on the title
const getBackgroundColor = computed(() => {
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-purple-500",
    "bg-red-500",
    "bg-yellow-500",
    "bg-indigo-500",
    "bg-pink-500",
    "bg-teal-500",
  ];

  if (!props.card.title) return colors[0];

  // Simple hash function to get consistent color for same title
  let hash = 0;
  for (let i = 0; i < props.card.title.length; i++) {
    hash = props.card.title.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
});

const highlightMatches = (text, searchTerm) => {
  if (!searchTerm.trim()) return text;
  const escapedSearch = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedSearch})`, "gi");
  return text.replace(regex, '<span class="search-highlight">$1</span>');
};
</script>

<template>
  <NuxtLink
    :to="card.link"
    class="dark:[box-shadow:0_0_.15em_.1em_rgba(63,22,104,0.5)] dark:hover:[box-shadow:0_0_.15em_.1em_rgba(98,69,116,0.8)] dark:bg-transparent bg-white p-5 rounded-xl text-center hover:[box-shadow:0_.0625rem_.75rem_0_rgba(38,57,82,.2)] [box-shadow:0_.3em_.3em_-.3em_hsla(0,0%,4%,.1),_0_0_0_.1rem_hsla(0,0%,4%,.08)] transition-all duration-500"
    :aria-label="card.link"
  >
    <!-- Conditional rendering: Show initial circle or image -->
    <div
      v-if="shouldShowInitial"
      class="mx-auto mb-4 w-24 h-24 flex items-center justify-center"
    >
      <div
        :class="[
          'w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg',
          getBackgroundColor,
        ]"
      >
        {{ firstLetter }}
      </div>
    </div>

    <NuxtImg
      v-else
      :src="card.img"
      width="96"
      height="96"
      :alt="card.alt || 'image'"
      class="mx-auto mb-4"
      loading="lazy"
      format="webp"
    />

    <!-- <NuxtImg
      v-else
      :src="card.img"
      width="96"
      height="96"
      :alt="card.alt || ''"
      class="mx-auto mb-4"
      loading="lazy"
      format="webp"
    /> -->

    <h2 class="font-semibold text-[var(--primary)] text-base mb-2" v-html="highlightMatches(card.title, searchTerm)" />

    <p
      class="text-sm !leading-[1.8] tracking-[0.5px]"
      v-html="highlightMatches(card.text, searchTerm)"
    />
  </NuxtLink>
</template>

<style scoped>
.search-highlight {
  @apply bg-yellow-200 dark:bg-yellow-800 px-1 rounded;
}
</style>
